import React from 'react';
import {
    Card,
    CardHeader,
    List,
    ListItemButton,
    ListItemText,
    Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IMenuResponse } from '../../../model/base/response/menu-response';
import { useNavigate as useReactRouterNavigate } from 'react-router-dom';

interface MegaMenuCardProps {
    menu: IMenuResponse;
    onNavigate: () => void;
}

export const MegaMenuCard: React.FC<MegaMenuCardProps> = ({ menu, onNavigate }) => {
    const navigate = useReactRouterNavigate();
    const allItems = menu.items || [];

    const handleItemClick = (url: string | undefined, isExternal: boolean | undefined) => {
        if (!url) return;

        if (isExternal) {
            window.open(url, '_blank', 'noopener,noreferrer');
        } else {
            // Garante que a URL começa com / para navegação absoluta
            const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
            // Usa navigate com replace para evitar concatenação
            navigate(normalizedUrl, { replace: false });
        }
        onNavigate();
    };

    return (
        <Card
            elevation={2}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s ease',
                '&:hover': {
                    boxShadow: 4,
                },
            }}
        >
            <CardHeader
                title={
                    <Typography variant="subtitle1" fontWeight={700} fontSize="0.95rem">
                        {menu.name}
                    </Typography>
                }
                sx={{
                    backgroundColor: 'primary.main',
                    color: 'white',
                    py: 1.5,
                    px: 2,
                }}
            />
            <List dense disablePadding sx={{ flexGrow: 1 }}>
                {allItems.map((item) => (
                    <ListItemButton
                        key={item.id}
                        onClick={() => handleItemClick(item.url, item.isExternal)}
                        sx={{
                            py: 1,
                            px: 2,
                            '&:hover': {
                                backgroundColor: 'primary.light',
                                '& .MuiListItemText-primary': {
                                    color: 'primary.dark',
                                    fontWeight: 600,
                                },
                            },
                        }}
                    >
                        <ListItemText
                            primary={item.name}
                            primaryTypographyProps={{
                                fontSize: '0.85rem',
                                fontWeight: 500,
                            }}
                        />
                        {item.isExternal && (
                            <OpenInNewIcon
                                fontSize="small"
                                sx={{ ml: 1, fontSize: '0.9rem', color: 'text.secondary' }}
                            />
                        )}
                    </ListItemButton>
                ))}
            </List>
        </Card>
    );
};
